import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  FC,
  useCallback,
  useMemo,
} from "react";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertColor } from "@mui/material/Alert";

interface SnackbarContextType {
  fetchSuccess: (message: string) => void;
  fetchError: (message: string) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

interface SnackbarProviderProps {
  children: ReactNode;
}

export const SnackbarProvider: FC<SnackbarProviderProps> = ({ children }) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<AlertColor>("success");

  const fetchSuccess = useCallback((msg: string) => {
    setMessage(msg);
    setSeverity("success");
    setOpenSnackbar(true);
  }, []);

  const fetchError = useCallback((msg: string) => {
    setMessage(msg);
    setSeverity("error");
    setOpenSnackbar(true);
  }, []);

  const closeSnackbar = () => {
    setOpenSnackbar(false);
  };

  const contextValue = useMemo(
    () => ({ fetchSuccess, fetchError }),
    [fetchSuccess, fetchError]
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert
          onClose={closeSnackbar}
          severity={severity}
          sx={{
            backgroundColor: severity === "success" ? "#388e3c" : "#d32f2f",
            color: "#fff",
          }}
        >
          {message}
        </MuiAlert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
